import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Checkbox } from "components/ui/checkbox";
import { Separator } from "components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import MultiSelect from "components/forms/MultiSelect";
import { cn } from "lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import { Link2, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

const CONTENT_TYPE_OPTIONS = [
  { key: "paragraph", label: "Paragraph", value: "paragraph" },
  { key: "h1", label: "Header 1", value: "h1" },
  { key: "h2", label: "Header 2", value: "h2" },
  { key: "checkbox_text", label: "Text with checkbox", value: "checkbox_text" },
  { key: "image", label: "Image", value: "image" },
];

const LANGUAGE_OPTIONS = [
  { key: "FR", value: "FR", text: "FR" },
  { key: "POR", value: "POR", text: "POR" },
  { key: "ES", value: "ES", text: "ES" },
];

function normalizeLangCode(code) {
  if (!code) return "";
  return String(code).toUpperCase();
}

function deepCloneScreens(screens) {
  try {
    return JSON.parse(JSON.stringify(screens || []));
  } catch (e) {
    return [];
  }
}

const emptyScreen = () => ({ id: uuidv4(), title: "", contents: [] });

const SectionTitle = ({ title, description }) => (
  <div className="space-y-1">
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    {description ? (
      <p className="text-xs text-muted-foreground">{description}</p>
    ) : null}
  </div>
);

const CheckboxField = ({ label, description, checked, onCheckedChange }) => (
  <label className="flex items-start gap-3">
    <Checkbox
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(!!value)}
      className="mt-0.5"
    />
    <span className="text-sm text-foreground">
      {label}
      {description ? (
        <span className="block text-xs text-muted-foreground">{description}</span>
      ) : null}
    </span>
  </label>
);

const ContentEditor = ({
  screenId,
  content,
  updateContent,
  removeContent,
  updateSelection,
  insertInlineLink,
  requiredLangs,
  moveContentUp,
  moveContentDown,
  isFirst,
  isLast,
}) => {
  if (!content) return null;
  const { id, type, payload } = content;

  const renderLanguageFields = ({
    placeholder,
    fieldKey,
    component: Component,
    props,
  }) => (
    <div className="mt-3 space-y-2">
      {(requiredLangs || []).map((lc) => {
        const norm = normalizeLangCode(lc);
        return (
          <Component
            key={`${id}-${norm}`}
            {...props}
            placeholder={`${placeholder} (${norm})`}
            value={payload?.[norm] || payload?.[`${fieldKey}_${norm.toLowerCase()}`] || ""}
            onChange={(event) =>
              updateContent(screenId, id, { [norm]: event.target.value })
            }
            className={cn("w-full", Component === Textarea && "min-h-[72px]")}
          />
        );
      })}
    </div>
  );

  const handleLinkInsert = () => insertInlineLink(screenId, id, payload?.text || "");

  return (
    <div className="space-y-4 rounded-lg border border-border/70 bg-card/40 p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">
          {type === "paragraph" && "Paragraph"}
          {type === "h1" && "Header 1"}
          {type === "h2" && "Header 2"}
          {type === "checkbox_text" && "Text with checkbox"}
          {type === "link" && "Link"}
          {type === "image" && "Image"}
        </h4>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => moveContentUp(screenId, id)}
            disabled={isFirst}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => moveContentDown(screenId, id)}
            disabled={isLast}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeContent(screenId, id)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {type === "paragraph" && (
        <>
          <Textarea
            rows={4}
            value={payload?.text || ""}
            placeholder="Paragraph text"
            onChange={(event) => updateContent(screenId, id, { text: event.target.value })}
            onSelect={(event) =>
              updateSelection(id, event.target.selectionStart ?? 0, event.target.selectionEnd ?? 0)
            }
          />
          {renderLanguageFields({
            placeholder: "Paragraph text",
            fieldKey: "text",
            component: Textarea,
            props: { rows: 3 },
          })}
          <div className="flex justify-between">
            <Button type="button" size="sm" variant="outline" onClick={handleLinkInsert}>
              <Link2 className="mr-2 h-4 w-4" /> Insert link/email token
            </Button>
          </div>
        </>
      )}

      {type === "h1" && (
        <>
          <Input
            value={payload?.text || ""}
            placeholder="Header text"
            onChange={(event) => updateContent(screenId, id, { text: event.target.value })}
          />
          {renderLanguageFields({
            placeholder: "Header text",
            fieldKey: "text",
            component: Input,
            props: {},
          })}
        </>
      )}

      {type === "h2" && (
        <>
          <Input
            value={payload?.text || ""}
            placeholder="Header text"
            onChange={(event) => updateContent(screenId, id, { text: event.target.value })}
          />
          {renderLanguageFields({
            placeholder: "Header text",
            fieldKey: "text",
            component: Input,
            props: {},
          })}
        </>
      )}

      {type === "link" && (
        <div className="space-y-3">
          <Input
            value={payload?.label || ""}
            placeholder="Label (optional)"
            onChange={(event) => updateContent(screenId, id, { label: event.target.value })}
          />
          {renderLanguageFields({
            placeholder: "Label",
            fieldKey: "label",
            component: Input,
            props: {},
          })}
          <Input
            value={payload?.url || ""}
            placeholder="URL or email (https://..., user@example.com)"
            onChange={(event) => updateContent(screenId, id, { url: event.target.value })}
          />
        </div>
      )}

      {type === "checkbox_text" && (
        <div className="space-y-3">
          <Input
            value={payload?.text || ""}
            placeholder="Text"
            onChange={(event) => updateContent(screenId, id, { text: event.target.value })}
            onSelect={(event) =>
              updateSelection(id, event.target.selectionStart ?? 0, event.target.selectionEnd ?? 0)
            }
          />
          {renderLanguageFields({
            placeholder: "Checkbox text",
            fieldKey: "text",
            component: Input,
            props: {},
          })}
          <CheckboxField
            label="Checked by default"
            checked={!!payload?.checkedByDefault}
            onCheckedChange={(checked) =>
              updateContent(screenId, id, { checkedByDefault: !!checked })
            }
          />
          <Button type="button" size="sm" variant="outline" onClick={handleLinkInsert}>
            <Link2 className="mr-2 h-4 w-4" /> Insert link/email token
          </Button>
        </div>
      )}

      {type === "image" && (
        <div className="space-y-3">
          <Input
            value={payload?.url || ""}
            placeholder="Image URL"
            onChange={(event) => updateContent(screenId, id, { url: event.target.value })}
          />
          <div className="flex gap-2">
            <Input
              type="number"
              value={payload?.height || ""}
              placeholder="Height (px)"
              onChange={(event) => updateContent(screenId, id, { height: parseInt(event.target.value) || null })}
              className="w-32"
            />
            <div className="flex items-center text-xs text-muted-foreground">
              pixels
            </div>
          </div>
          {payload?.url && (
            <div className="mt-3">
              <img
                src={payload.url}
                alt="Preview"
                style={{ height: payload.height ? `${payload.height}px` : 'auto', maxWidth: '100%' }}
                className="rounded border"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CustomIntroScreensModal = ({ open, onClose, existingScreens, existingDefaults, onSave }) => {
  const [screens, setScreens] = useState([]);
  const [defaults, setDefaults] = useState({
    default_intro: false,
    default_privacy: false,
    default_private_response: false,
  });
  const [selections, setSelections] = useState({});
  const [requiredLangs, setRequiredLangs] = useState([]);
  const [expandedScreenId, setExpandedScreenId] = useState(null);

  useEffect(() => {
    if (open) {
      setScreens(deepCloneScreens(existingScreens));
      setDefaults({
        default_intro: !!existingDefaults?.default_intro,
        default_privacy: !!existingDefaults?.default_privacy,
        default_private_response: !!existingDefaults?.default_private_response,
      });
      const initLangs = Array.isArray(existingDefaults?.languages)
        ? existingDefaults.languages
        : [];
      setRequiredLangs(initLangs);
      const firstScreen = (existingScreens && existingScreens[0]) || null;
      setExpandedScreenId(firstScreen ? firstScreen.id : null);
    }
  }, [open, existingScreens, existingDefaults]);

  function ensureLangFieldsOnScreens(nextLangs, baseScreens) {
    const langs = (nextLangs || []).map(normalizeLangCode);
    return (baseScreens || []).map((screen) => {
      const next = { ...screen };
      langs.forEach((lc) => {
        if (next[lc] === undefined) next[lc] = "";
      });
      next.contents = (next.contents || []).map((content) => {
        const payload = { ...(content.payload || {}) };
        if (["paragraph", "h1", "h2", "checkbox_text", "link", "image"].includes(content.type)) {
          langs.forEach((lc) => {
            if (payload[lc] === undefined) payload[lc] = "";
          });
        }
        return { ...content, payload };
      });
      return next;
    });
  }

  const addScreen = () => {
    setScreens((prev) => {
      const next = ensureLangFieldsOnScreens(requiredLangs, [...(prev || []), emptyScreen()]);
      const latest = next[next.length - 1];
      if (latest) setExpandedScreenId(latest.id);
      return next;
    });
  };

  const removeScreen = (screenId) => {
    setScreens((prev) => (prev || []).filter((screen) => screen.id !== screenId));
    setExpandedScreenId((current) => {
      if (current === screenId) {
        const remaining = screens.filter((screen) => screen.id !== screenId);
        return remaining.length ? remaining[0].id : null;
      }
      return current;
    });
  };

  const updateScreenTitle = (screenId, title) => {
    setScreens((prev) =>
      (prev || []).map((screen) => (screen.id === screenId ? { ...screen, title } : screen))
    );
  };

  const updateScreenTitleLang = (screenId, langCode, title) => {
    const norm = normalizeLangCode(langCode);
    setScreens((prev) =>
      (prev || []).map((screen) => (screen.id === screenId ? { ...screen, [norm]: title } : screen))
    );
  };

  const addContent = (screenId, type) => {
    if (!type) return;
    const baseContent = { id: uuidv4(), type };
    let payload = {};
    if (type === "paragraph") payload = { text: "", link: null };
    if (type === "h1") payload = { text: "" };
    if (type === "h2") payload = { text: "" };
    if (type === "checkbox_text") payload = { text: "", checkedByDefault: false, link: null };
    if (type === "link") payload = { label: "", url: "" };
    if (type === "image") payload = { url: "", height: null };

    payload = ensureLangFieldsOnScreens(requiredLangs, [{ id: "tmp", contents: [{ id: "tmpc", type, payload }] }])[0]
      .contents[0].payload;

    setScreens((prev) =>
      (prev || []).map((screen) =>
        screen.id === screenId
          ? { ...screen, contents: [...(screen.contents || []), { ...baseContent, payload }] }
          : screen
      )
    );
  };

  const removeContent = (screenId, contentId) => {
    setScreens((prev) =>
      (prev || []).map((screen) =>
        screen.id === screenId
          ? { ...screen, contents: (screen.contents || []).filter((content) => content.id !== contentId) }
          : screen
      )
    );
  };

  const moveContentUp = (screenId, contentId) => {
    setScreens((prev) =>
      (prev || []).map((screen) => {
        if (screen.id !== screenId) return screen;
        const contents = [...(screen.contents || [])];
        const index = contents.findIndex((content) => content.id === contentId);
        if (index > 0) {
          [contents[index - 1], contents[index]] = [contents[index], contents[index - 1]];
        }
        return { ...screen, contents };
      })
    );
  };

  const moveContentDown = (screenId, contentId) => {
    setScreens((prev) =>
      (prev || []).map((screen) => {
        if (screen.id !== screenId) return screen;
        const contents = [...(screen.contents || [])];
        const index = contents.findIndex((content) => content.id === contentId);
        if (index < contents.length - 1) {
          [contents[index], contents[index + 1]] = [contents[index + 1], contents[index]];
        }
        return { ...screen, contents };
      })
    );
  };

  const updateContent = (screenId, contentId, update) => {
    setScreens((prev) =>
      (prev || []).map((screen) => {
        if (screen.id !== screenId) return screen;
        return {
          ...screen,
          contents: (screen.contents || []).map((content) =>
            content.id === contentId
              ? { ...content, payload: { ...content.payload, ...update } }
              : content
          ),
        };
      })
    );
  };

  const updateSelection = (contentId, start, end) => {
    setSelections((prev) => ({ ...prev, [contentId]: { start: start || 0, end: end || 0 } }));
  };

  const insertInlineLink = (screenId, contentId, currentText) => {
    const text = currentText || "";
    const selection = selections[contentId] || { start: text.length, end: text.length };
    const start = Math.max(0, Math.min(selection.start, text.length));
    const end = Math.max(0, Math.min(selection.end, text.length));
    const hasSelection = end > start;
    const label = hasSelection
      ? text.slice(start, end)
      : window.prompt("Link label (visible text)", "");
    if (label == null) return;
    if (!hasSelection && label.length === 0) return;
    const input = window.prompt("Link or email (https://..., user@example.com)", "");
    if (input == null || input.trim().length === 0) return;
    let uri = input.trim();
    const isHttp = /^https?:\/\//i.test(uri);
    const isMailto = /^mailto:/i.test(uri);
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(uri);
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(uri);
    if (isMailto) {
      // leave as is
    } else if (isEmail) {
      uri = `mailto:${uri}`;
    } else if (isHttp) {
      // keep http(s)
    } else if (!hasScheme) {
      uri = `https://${uri}`;
    }
    const before = text.slice(0, start);
    const after = text.slice(end);
    const token = `{{${label}|${uri}}}`;
    updateContent(screenId, contentId, { text: `${before}${token}${after}` });
  };

  useEffect(() => {
    setScreens((prev) => ensureLangFieldsOnScreens(requiredLangs, prev));
  }, [requiredLangs]);

  const handleSave = () => {
    const normalized = (screens || []).map((screen) => ({
      id: screen.id || uuidv4(),
      title: screen.title || "",
      ...(() => {
        const acc = {};
        (requiredLangs || []).forEach((lc) => {
          const norm = normalizeLangCode(lc);
          if (screen[norm] !== undefined && screen[norm] !== null) acc[norm] = screen[norm];
        });
        return acc;
      })(),
      contents: (screen.contents || []).map((content) => ({
        id: content.id || uuidv4(),
        type: content.type,
        payload: content.payload || {},
      })),
    }));
    onSave({ screens: normalized, defaults: { ...defaults, languages: requiredLangs } });
  };

  const closeDialog = (isOpen) => {
    if (!isOpen) {
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent className="max-h-[90vh] w-full max-w-6xl overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Introduction Screens</DialogTitle>
          <DialogDescription>
            Add multiple introduction screens and manage their content and translations.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 lg:grid lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] lg:gap-8">
          <section className="space-y-6 rounded-lg border border-border/60 bg-muted/20 p-4">
            <div className="space-y-4">
              <SectionTitle
                title="Default screens"
                description="Toggle which default screens should be included."
              />
              <div className="grid gap-3">
                <CheckboxField
                  label="Include default intro"
                  checked={!!defaults.default_intro}
                  onCheckedChange={(checked) =>
                    setDefaults((prev) => ({ ...prev, default_intro: !!checked }))
                  }
                />
                <CheckboxField
                  label="Include default privacy"
                  checked={!!defaults.default_privacy}
                  onCheckedChange={(checked) =>
                    setDefaults((prev) => ({ ...prev, default_privacy: !!checked }))
                  }
                />
                <CheckboxField
                  label="Include private response screen"
                  checked={!!defaults.default_private_response}
                  onCheckedChange={(checked) =>
                    setDefaults((prev) => ({ ...prev, default_private_response: !!checked }))
                  }
                />
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-4">
              <SectionTitle
                title="Required languages"
                description="Languages in addition to English that require translated content."
              />
              <MultiSelect
                options={LANGUAGE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  text: opt.text,
                  label: opt.text,
                }))}
                value={requiredLangs}
                onChange={(value) => setRequiredLangs(value || [])}
                placeholder="Select languages"
              />
            </div>
          </section>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border/60">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Screens ({screens?.length || 0})
                </h3>
                <p className="text-xs text-muted-foreground">
                  Add screens, then populate with content blocks.
                </p>
              </div>
              <Button type="button" size="sm" onClick={addScreen}>
                <Plus className="mr-2 h-4 w-4" /> Add screen
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
              {(screens || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                  <span>No screens yet.</span>
                  <span>Click "Add screen" to create one.</span>
                </div>
              ) : (
                <Accordion
                  type="single"
                  collapsible
                  value={expandedScreenId || undefined}
                  onValueChange={(value) => setExpandedScreenId(value || null)}
                  className="space-y-4"
                >
                  {(screens || []).map((screen, index) => (
                    <AccordionItem key={screen.id} value={screen.id} className="border border-border/70 rounded-lg bg-background">
                      <AccordionTrigger className="px-4 py-3 text-left hover:no-underline">
                        <div className="flex flex-1 flex-col items-start gap-1 text-sm">
                          <span className="font-semibold text-foreground">Screen {index + 1}</span>
                          <span className="text-xs text-muted-foreground">
                            {screen.title?.trim() ? screen.title : "No title set"}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeScreen(screen.id);
                          }}
                          className="ml-2 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 pt-2">
                        <div className="space-y-4">
                          <div className="space-y-3">
                            <Input
                              value={screen.title || ""}
                              placeholder="Screen title (optional)"
                              onChange={(event) => updateScreenTitle(screen.id, event.target.value)}
                            />
                            {(requiredLangs || []).map((lc) => (
                              <Input
                                key={`${screen.id}-title-${lc}`}
                                value={screen?.[`title_${normalizeLangCode(lc)}`] || ""}
                                placeholder={`Screen title (${normalizeLangCode(lc)})`}
                                onChange={(event) =>
                                  updateScreenTitleLang(screen.id, lc, event.target.value)
                                }
                              />
                            ))}
                          </div>

                          <Separator />

                          <div className="flex flex-wrap items-center gap-3">
                            <Select onValueChange={(value) => addContent(screen.id, value)}>
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Add content block" />
                              </SelectTrigger>
                              <SelectContent>
                                {CONTENT_TYPE_OPTIONS.map((option) => (
                                  <SelectItem key={option.key} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Choose a block type to append to this screen.
                            </p>
                          </div>

                          {(screen.contents || []).length === 0 ? (
                            <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                              No content blocks yet.
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {(screen.contents || []).map((content, contentIndex) => (
                                <ContentEditor
                                  key={content.id}
                                  screenId={screen.id}
                                  content={content}
                                  updateContent={updateContent}
                                  removeContent={removeContent}
                                  updateSelection={updateSelection}
                                  insertInlineLink={insertInlineLink}
                                  requiredLangs={requiredLangs}
                                  moveContentUp={moveContentUp}
                                  moveContentDown={moveContentDown}
                                  isFirst={contentIndex === 0}
                                  isLast={contentIndex === (screen.contents || []).length - 1}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </section>
        </div>
        <DialogFooter className="border-t border-border/60 bg-muted/10 px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onClose?.()}>
            Close
          </Button>
          <Button type="button" onClick={handleSave}>
            Save intro screens
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomIntroScreensModal;


